/**
 * 任务领域模型类型定义
 * 
 * 核心业务实体，贯穿整个应用（组件、Store、API）
 * 使用判别联合模式区分单次和批量任务
 * 
 * @version 2.0.0 - 重构版本
 * @author Frontend Team
 * @since 2025-10-22
 */

import type { BaseEntity } from './userTypes'

// ============= 基础类型 =============

/**
 * 任务状态（统一状态机）
 * 
 * 状态流转：
 * created → queued → processing → post_processing → completed
 *                        ↓
 *                  failed (failurePhase: 'processing' | 'post_processing')
 *                        ↓
 *                    cancelled (用户终止)
 */
export const TASK_STATUS = {
  CREATED: 'created',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  POST_PROCESSING: 'post_processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  PROCESSING_FAILED: 'processing_failed',
  POST_FAILED: 'post_failed',
  CANCELLED: 'cancelled'
} as const

export type TaskStatus = typeof TASK_STATUS[keyof typeof TASK_STATUS]

/**
 * 任务类型
 */
export const TASK_TYPE = {
  SINGLE: 'single',
  BATCH: 'batch'
} as const

export type TaskType = typeof TASK_TYPE[keyof typeof TASK_TYPE]

/**
 * 任务优先级
 */
export const TASK_PRIORITY = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high'
} as const

export type TaskPriority = typeof TASK_PRIORITY[keyof typeof TASK_PRIORITY]

/**
 * 产物类型（生成的输出物）
 */
export const ARTIFACT_KIND = {
  AUDIO: 'audio',
  ZIP: 'zip',
  MERGED_AUDIO: 'merged_audio',
  SUBTITLE: 'subtitle',
  LOG: 'log'
} as const

export type ArtifactKind = typeof ARTIFACT_KIND[keyof typeof ARTIFACT_KIND]

/**
 * 失败阶段
 */
export const FAILURE_PHASE = {
  PROCESSING: 'processing',
  POST_PROCESSING: 'post_processing'
} as const

export type FailurePhase = typeof FAILURE_PHASE[keyof typeof FAILURE_PHASE]

// ============= 产物（Artifact）=============

/**
 * 产物接口 - 代表任务生成的输出物
 */
export interface Artifact {
  id: string
  kind: ArtifactKind
  filename: string
  url: string
  durationSec?: number
  size?: number
  tags?: string[]
  favorite?: boolean
  createdAt: string
  meta?: Record<string, any>
}

// ============= 任务项（TaskItem）=============

/**
 * 任务项接口 - 批量任务中的单个任务
 * 
 * 用途：
 * - 批量任务由多个任务项组成
 * - 每个任务项独立处理和跟踪
 * - 支持单个任务项重试
 */
export interface TaskItem {
  id: string
  status: TaskStatus
  progress: number
  text: string
  voice: string
  audio?: Artifact
  errorMsg?: string
  createdAt: string
  completedAt?: string
}

// ============= 权益快照 =============

/**
 * 权益快照 - 记录任务执行时的用户权益状态
 */
export interface EntitlementSnapshot {
  tier: 'free' | 'pro' | 'enterprise'
  quotaBefore: number
  quotaAfter: number
}

// ============= 任务实体（使用判别联合）=============

/**
 * 单次任务
 */
export interface SingleTask extends BaseEntity {
  taskId: string  // 业务ID，用于SSE订阅等
  type: 'single'
  status: TaskStatus
  priority: TaskPriority
  title: string
  description?: string
  progress: number
  
  // 单次任务特有字段
  text: string
  voice: string

  // 任务项（后端实际数据结构）
  items?: TaskItem[]

  // 产物与下载
  artifacts?: Artifact[]
  downloadUrl?: string
  
  // 时间估算
  estimatedTime?: number
  remainingTime?: number
  
  // 权益快照
  entitlementSnapshot?: EntitlementSnapshot
  
  // 失败信息
  error?: string
  failurePhase?: FailurePhase
  
  // 元数据
  config?: Record<string, any>
  metadata?: Record<string, any>
}

/**
 * 批量任务
 */
export interface BatchTask extends BaseEntity {
  taskId: string  // 业务ID，用于SSE订阅等
  type: 'batch'
  status: TaskStatus
  priority: TaskPriority
  title: string
  description?: string
  progress: number
  
  // 批量任务特有字段
  totalItems: number
  completedItems: number
  failedItems: number
  items?: TaskItem[]
  
  // 产物与下载
  artifacts?: Artifact[]
  downloadUrl?: string
  
  // 时间估算
  estimatedTime?: number
  remainingTime?: number
  
  // 权益快照
  entitlementSnapshot?: EntitlementSnapshot
  
  // 失败信息
  error?: string
  failurePhase?: FailurePhase
  
  // 元数据
  config?: Record<string, any>
  metadata?: Record<string, any>
}

/**
 * 任务 - 使用判别联合
 * 
 * 设计原则：
 * - 单次任务：type='single'，text/voice 字段有值，items 为空
 * - 批量任务：type='batch'，items 有值，totalItems/completedItems/failedItems 有值
 * - 通过 type 字段自动获得完整的类型提示
 */
export type Task = SingleTask | BatchTask

// ============= UI 选项派生 =============

/**
 * 任务状态选项（从常量派生）
 */
export const TASK_STATUS_OPTIONS = Object.entries(TASK_STATUS).map(([key, value]) => ({
  label: key,
  value
}))

/**
 * 任务类型选项（从常量派生）
 */
export const TASK_TYPE_OPTIONS = Object.entries(TASK_TYPE).map(([key, value]) => ({
  label: key,
  value
}))

/**
 * 任务优先级选项（从常量派生）
 */
export const TASK_PRIORITY_OPTIONS = Object.entries(TASK_PRIORITY).map(([key, value]) => ({
  label: key,
  value
}))

// ============= 类型守卫 =============

/**
 * 判断是否为单次任务
 */
export function isSingleTask(task: Task): task is SingleTask {
  return task.type === 'single'
}

/**
 * 判断是否为批量任务
 */
export function isBatchTask(task: Task): task is BatchTask {
  return task.type === 'batch'
}

